/**
 * Parse a date string (YYYY-MM-DD) as local date to avoid timezone shifts.
 * When JavaScript's new Date("YYYY-MM-DD") parses a date string, it interprets
 * it as UTC midnight, which can shift the date when displayed in local time.
 * This function parses the date components directly to create a local date.
 *
 * @param {string|Date} dateInput - Date string in YYYY-MM-DD format or Date object
 * @returns {Date|null} - Local Date object or null if invalid
 */
export function parseLocalDate(dateInput) {
    if (!dateInput) return null;

    // If it's already a Date object, return it directly
    if (dateInput instanceof Date) {
        return Number.isNaN(dateInput.getTime()) ? null : dateInput;
    }

    // If it's a string in YYYY-MM-DD format, parse as local date
    if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateInput)) {
        const datePart = dateInput.split('T')[0];
        const [year, month, day] = datePart.split('-').map(Number);

        if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
            return new Date(year, month - 1, day);
        }
    }

    // Fallback to standard Date parsing
    const d = new Date(dateInput);
    return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Parse a date and return its components along with the Date object.
 * Useful when you need both the parsed date and its year/month/day values.
 *
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {{ year: number, month: number, day: number, date: Date } | null}
 */
export function parseDateAsLocal(dateInput) {
    if (!dateInput) return null;

    // If it's already a Date object, extract local date components directly
    if (dateInput instanceof Date) {
        if (Number.isNaN(dateInput.getTime())) return null;
        return {
            year: dateInput.getFullYear(),
            month: dateInput.getMonth() + 1,
            day: dateInput.getDate(),
            date: dateInput,
        };
    }

    // If it's a string in YYYY-MM-DD format, parse as local date
    const dateStr = String(dateInput).split('T')[0];
    const parts = dateStr.split('-');

    if (parts.length >= 3) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10); // 1-12
        const day = parseInt(parts[2], 10);

        if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
            return {
                year,
                month,
                day,
                date: new Date(year, month - 1, day),
            };
        }
    }

    // Fallback to Date parsing for other formats
    const d = new Date(dateInput);
    if (Number.isNaN(d.getTime())) return null;

    return {
        year: d.getFullYear(),
        month: d.getMonth() + 1,
        day: d.getDate(),
        date: d,
    };
}
